/**
 * Report HTML Utilities
 *
 * Pure utility functions for parsing and manipulating assessment report HTML.
 * Used by ReportBlockView for block-level rendering and move operations.
 */

export type SectionType = 'positive' | 'improvement' | 'agreements' | 'conclusion';

export interface ParsedBlock {
  html: string;
  title: string;
}

export interface ParsedSection {
  sectionType: SectionType;
  headerHtml: string;
  summaryHtml: string;
  blocks: ParsedBlock[];
  /** For conclusion section: the conclusion-box HTML */
  conclusionBoxHtml: string;
  /** For sections with no items: the no-items placeholder HTML */
  noItemsHtml: string;
}

export interface ParsedReport {
  css: string;
  headerHtml: string;
  employeeInfoHtml: string;
  sections: ParsedSection[];
  signatureHtml: string;
  footerHtml: string;
}

/**
 * Identify section type from the section-icon class
 */
function identifySectionType(sectionEl: Element): SectionType {
  const icon = sectionEl.querySelector('.section-icon');
  if (!icon) return 'conclusion';

  if (icon.classList.contains('positive')) return 'positive';
  if (icon.classList.contains('improvement')) return 'improvement';
  if (icon.classList.contains('agreements')) return 'agreements';
  return 'conclusion';
}

/**
 * Extract block title from card HTML
 */
function extractBlockTitle(cardEl: Element): string {
  // item-card: look for .item-name
  const itemName = cardEl.querySelector('.item-name');
  if (itemName) return itemName.textContent?.trim() || 'Onbekend item';

  // agreement-card: look for .agreement-description
  const agreementDesc = cardEl.querySelector('.agreement-description');
  if (agreementDesc) return agreementDesc.textContent?.trim() || 'Onbekende afspraak';

  return 'Onbekend blok';
}

/**
 * Parse report HTML into structured data for block-level rendering.
 * Returns null if the HTML structure cannot be reliably parsed.
 */
export function parseReportForBlockView(html: string): ParsedReport | null {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Extract CSS
    const styleEl = doc.querySelector('style');
    const css = styleEl?.textContent || '';

    // Extract structural elements
    const header = doc.querySelector('.header');
    const employeeInfo = doc.querySelector('.employee-info');
    const signature = doc.querySelector('.signature-section');
    const footer = doc.querySelector('.footer');

    if (!header) return null;

    // Extract sections
    const sectionEls = doc.querySelectorAll('.section');
    if (sectionEls.length === 0) return null;

    const sections: ParsedSection[] = [];

    for (const sectionEl of sectionEls) {
      const sectionType = identifySectionType(sectionEl);

      // Extract section header
      const sectionHeader = sectionEl.querySelector('.section-header');
      const headerHtml = sectionHeader?.outerHTML || '';

      // Extract summary
      const summary = sectionEl.querySelector('.summary');
      const summaryHtml = summary?.outerHTML || '';

      // Extract blocks (item-card or agreement-card)
      const blocks: ParsedBlock[] = [];
      const itemsList = sectionEl.querySelector('.items-list');

      if (itemsList) {
        const cards = itemsList.querySelectorAll('.item-card, .agreement-card');
        for (const card of cards) {
          blocks.push({
            html: card.outerHTML,
            title: extractBlockTitle(card),
          });
        }
      }

      // Conclusion section has a conclusion-box instead of item cards
      const conclusionBox = sectionEl.querySelector('.conclusion-box');
      const conclusionBoxHtml = conclusionBox?.outerHTML || '';

      // Capture no-items placeholder if present
      const noItemsEl = sectionEl.querySelector('.no-items');
      const noItemsHtml = noItemsEl?.outerHTML || '';

      sections.push({
        sectionType,
        headerHtml,
        summaryHtml,
        blocks,
        conclusionBoxHtml,
        noItemsHtml,
      });
    }

    return {
      css,
      headerHtml: header.outerHTML,
      employeeInfoHtml: employeeInfo?.outerHTML || '',
      sections,
      signatureHtml: signature?.outerHTML || '',
      footerHtml: footer?.outerHTML || '',
    };
  } catch {
    return null;
  }
}

/**
 * Get the display label for a section type
 */
export function getSectionLabel(sectionType: SectionType): string {
  switch (sectionType) {
    case 'positive': return 'Wat ging goed';
    case 'improvement': return 'Wat kan er beter';
    case 'agreements': return 'Afspraken';
    case 'conclusion': return 'Conclusie';
  }
}

/**
 * Get valid target sections for a block move based on the source section type.
 *
 * Rules:
 * - positive ↔ improvement: free movement
 * - agreements: reorder within only
 * - conclusion: no movable blocks
 */
export function getValidTargetSections(sourceSectionType: SectionType): SectionType[] {
  switch (sourceSectionType) {
    case 'positive':
      return ['positive', 'improvement'];
    case 'improvement':
      return ['positive', 'improvement'];
    case 'agreements':
      return ['agreements'];
    case 'conclusion':
      return [];
  }
}

/**
 * No-items placeholder text per section type
 */
function getNoItemsText(sectionType: SectionType): string {
  switch (sectionType) {
    case 'positive': return 'Geen specifieke items met hoge score';
    case 'improvement': return 'Geen specifieke verbeterpunten geïdentificeerd';
    case 'agreements': return 'Geen afspraken vastgelegd';
    default: return '';
  }
}

/**
 * Map section type to the nth .section element in the document.
 * Sections appear in order: positive, improvement, agreements, conclusion.
 */
function getSectionIndex(sectionType: SectionType): number {
  switch (sectionType) {
    case 'positive': return 0;
    case 'improvement': return 1;
    case 'agreements': return 2;
    case 'conclusion': return 3;
  }
}

/**
 * Move a block from one position to another within the HTML document.
 * Returns the modified full HTML string.
 *
 * @param html - The full report HTML
 * @param fromSection - Source section type
 * @param fromIndex - Block index within source section
 * @param toSection - Target section type
 * @param toIndex - Target position (0 = first position, N = after Nth block)
 */
export function moveBlockInHtml(
  html: string,
  fromSection: SectionType,
  fromIndex: number,
  toSection: SectionType,
  toIndex: number,
): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const sections = doc.querySelectorAll('.section');
  const fromSectionEl = sections[getSectionIndex(fromSection)];
  const toSectionEl = sections[getSectionIndex(toSection)];

  if (!fromSectionEl || !toSectionEl) return html;

  // Get source items-list
  let fromItemsList = fromSectionEl.querySelector('.items-list');
  if (!fromItemsList) return html;

  // Get the block to move
  const fromCards = fromItemsList.querySelectorAll('.item-card, .agreement-card');
  const blockToMove = fromCards[fromIndex];
  if (!blockToMove) return html;

  // Remove block from source
  blockToMove.remove();

  // Check if source section is now empty
  const remainingCards = fromItemsList.querySelectorAll('.item-card, .agreement-card');
  if (remainingCards.length === 0) {
    // Replace items-list content with no-items placeholder
    const noItems = doc.createElement('div');
    noItems.className = 'no-items';
    noItems.textContent = getNoItemsText(fromSection);
    fromItemsList.replaceWith(noItems);
  }

  // Ensure target section has an items-list
  let toItemsList = toSectionEl.querySelector('.items-list');
  if (!toItemsList) {
    // Remove no-items placeholder if present
    const noItems = toSectionEl.querySelector('.no-items');
    if (noItems) {
      // Create items-list and replace no-items
      toItemsList = doc.createElement('div');
      toItemsList.className = 'items-list';
      noItems.replaceWith(toItemsList);
    } else {
      // Create items-list and append to section
      toItemsList = doc.createElement('div');
      toItemsList.className = 'items-list';
      toSectionEl.appendChild(toItemsList);
    }
  } else {
    // Remove no-items if it exists inside items-list
    const noItems = toItemsList.querySelector('.no-items');
    if (noItems) noItems.remove();
  }

  // Insert block at target position
  const toCards = toItemsList.querySelectorAll('.item-card, .agreement-card');
  if (toIndex >= toCards.length) {
    toItemsList.appendChild(blockToMove);
  } else {
    toItemsList.insertBefore(blockToMove, toCards[toIndex] ?? null);
  }

  // Serialize back to full HTML
  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
}

/**
 * Delete a block from a section in the HTML document.
 * Returns the modified full HTML string.
 */
export function deleteBlockInHtml(
  html: string,
  sectionType: SectionType,
  blockIndex: number,
): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const sections = doc.querySelectorAll('.section');
  const sectionEl = sections[getSectionIndex(sectionType)];
  if (!sectionEl) return html;

  const itemsList = sectionEl.querySelector('.items-list');
  if (!itemsList) return html;

  const cards = itemsList.querySelectorAll('.item-card, .agreement-card');
  const blockToDelete = cards[blockIndex];
  if (!blockToDelete) return html;

  blockToDelete.remove();

  // Check if section is now empty
  const remainingCards = itemsList.querySelectorAll('.item-card, .agreement-card');
  if (remainingCards.length === 0) {
    const noItems = doc.createElement('div');
    noItems.className = 'no-items';
    noItems.textContent = getNoItemsText(sectionType);
    itemsList.replaceWith(noItems);
  }

  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
}
